// Переезд грязного черновика блока на тот же блок другого источника (черновик ⇄ живой).
import type { Block } from '@/types/blocks'
import { sameJson } from '@/lib/sameJson'

/** Серверная мета: берётся только у нового блока, разницей автора не бывает. */
const META = new Set(['id', 'originId', 'order', 'createdAt', 'updatedAt'])

/** Единственные ключи, сливаемые вглубь; всё остальное (в т.ч. вложенный `article`) атомарно. */
const DEEP_KEYS = new Set(['body', 'bodyEn'])

type Rec = Record<string, unknown>

const isPlainObject = (v: unknown): v is Rec =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** Ключи `prev`, разошедшиеся с `seen`, кладутся поверх `onto`. Объекты (`body`, `bodyEn`)
 *  сливаются на один уровень вглубь, массивы атомарны: вложенные id у разных источников
 *  разные, и сравнивать можно только `prev` с `seen` (это один источник). */
const overlay = (onto: Rec, prev: Rec, seen: Rec, deep: boolean): Rec => {
  const out: Rec = { ...onto }
  for (const key of Object.keys(prev)) {
    if (deep && META.has(key)) continue
    if (sameJson(prev[key], seen[key])) continue
    const [p, s, o] = [prev[key], seen[key], onto[key]]
    out[key] =
      deep && DEEP_KEYS.has(key) && isPlainObject(p) && isPlainObject(s) && isPlainObject(o)
        ? overlay(o, p, s, false)
        : p
  }
  return out
}

/**
 * Грязный черновик того же блока под новым id: новый блок + несохранённая разница автора
 * (поля, разошедшиеся с ОСНОВОЙ черновика `lastSeen`). Так отмена черновика не возвращает
 * в форму уже отменённый текст, а несохранённая правка переживает переезд. При создании и
 * слиянии черновика новый блок по содержимому равен основе — результат тот же, что у
 * переноса тела целиком. Без основы (`null`) — прежний перенос тела целиком.
 *
 * Вложенные id (варианты квиза, пары, кнопки) из разницы не снимаются намеренно: бэк их на
 * записи не читает — схемы записи (`QuizAnswerBase`, `MatchPairBase`, `BlockButtonBase`) id
 * не содержат, варианты, пары и кнопки пересоздаются из тела целиком (`apply_block_update`).
 */
export const carryDiff = (prev: Block, lastSeen: Block | null, block: Block): Block => {
  const meta = { id: block.id, originId: block.originId, order: block.order }
  if (!lastSeen) return { ...prev, ...meta } as Block
  return overlay(
    block as unknown as Rec,
    prev as unknown as Rec,
    lastSeen as unknown as Rec,
    true,
  ) as unknown as Block
}
